import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../../Redux/hooks';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { routes } from '../../../Utils/routes';
import './StarterChecklist.css';
import { selectTaughtStudents } from '../../../Redux/selectors';

const STEPS: { key: string; label: TranslationKey; to: string }[] = [
  { key: 'addStudent', label: 'starterAddStudent', to: routes.students },
  { key: 'addSlot', label: 'starterAddSlot', to: routes.students },
  { key: 'addLesson', label: 'starterAddLesson', to: routes.lessons }
];

/**
 * The three things that have to happen before the application is any use.
 *
 * It disappears on its own once they are all done. There is no dismiss button
 * on purpose: a checklist you have to close is one more thing to do, and one
 * that lingers after it is finished becomes furniture.
 */
export function StarterChecklist() {
  const { tr } = useTranslation();
  // The first step is adding a student, and adding a conservatory to bill
  // is not that step: the checklist would tick itself off before the tutor
  // had anybody to teach.
  const students = useAppSelector(selectTaughtStudents);
  const lessons = useAppSelector(state => state.lessons.items);
  const ready = useAppSelector(state => state.students.state === 'ready');

  const done = useMemo(() => ({
    addStudent: students.length > 0,
    addSlot: students.some(student => (student.slots?.length ?? 0) > 0),
    addLesson: lessons.length > 0
  }), [students, lessons]);

  // Nothing to show before the roster has loaded, and nothing to show once
  // every step is behind the tutor.
  if (!ready) return null;
  if (STEPS.every(step => done[step.key as keyof typeof done])) return null;

  return (
    <section className="starter" aria-label={tr('starterTitle')}>
      <h2>{tr('starterTitle')}</h2>
      <ol className="starter-steps">
        {STEPS.map(step => {
          const complete = done[step.key as keyof typeof done];
          return (
            <li key={step.key} className={complete ? 'is-done' : ''}>
              <span className="starter-mark" aria-hidden="true">{complete ? '✓' : ''}</span>
              {complete
                ? <span>{tr(step.label)}</span>
                : <Link to={step.to}>{tr(step.label)}</Link>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
